import { useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useI18n } from '../i18n/useI18n'

/**
 * #1357 予約日を選ぶ月グリッド。
 *
 * ## なぜチップの平置きをやめたか
 *
 * 日付を全部チップで並べる形は、受付期間を伸ばすと破綻する:
 *
 * ```
 * 14 日 → 14 チップ（2 行）
 * 30 日 → 31 チップ（5 行）
 * 90 日 → 91 チップ（15 行）  ← 選べたものではない
 * ```
 *
 * `bookableDaysAhead` は最大 180 日まで設定できるので、平置きだと
 * **設定できるのに使えない**状態になる。
 *
 * 月グリッドなら何ヶ月先でも見た目が変わらず、**曜日が縦に揃う**ので
 * 「来月の第 3 土曜」のような探し方ができる（チップ列ではできない）。
 * 食べログ / ぐるなび / TableCheck もこの形。
 */

type Props = {
  /** 選択中の日付 (YYYY-MM-DD)。未選択は '' */
  value: string
  onChange: (date: string) => void
  /** 予約を受け付ける最終日 (YYYY-MM-DD) */
  maxDate: string
  /** その日が選べるか。定休日・受付期間外の判定は呼び出し側が持つ */
  isEnabled: (date: string) => boolean
}

const toDateStr = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

export default function MonthCalendar({ value, onChange, maxDate, isEnabled }: Props) {
  const { t } = useI18n()
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  // 表示中の月。選択済みならその月から開くと、戻ってきたときに迷わない。
  const [cursor, setCursor] = useState(() => {
    const base = value ? new Date(`${value}T00:00:00`) : today
    return new Date(base.getFullYear(), base.getMonth(), 1)
  })

  const last = new Date(`${maxDate}T00:00:00`)
  const canPrev = cursor > new Date(today.getFullYear(), today.getMonth(), 1)
  const canNext = cursor < new Date(last.getFullYear(), last.getMonth(), 1)

  // 月初の曜日ぶんだけ先頭を空ける（曜日を縦に揃えるため）
  const firstWeekday = cursor.getDay()
  const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate()
  const cells: (Date | null)[] = [
    ...Array.from({ length: firstWeekday }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => new Date(cursor.getFullYear(), cursor.getMonth(), i + 1)),
  ]

  const shift = (delta: number) =>
    setCursor((c) => new Date(c.getFullYear(), c.getMonth() + delta, 1))

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <button
          onClick={() => shift(-1)}
          disabled={!canPrev}
          aria-label={t('reservationCalendar.prevMonth')}
          className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 tap-effect disabled:opacity-30"
        >
          <ChevronLeft size={16} />
        </button>
        <p className="font-display text-sm font-bold text-text-primary">
          {t('reservationCalendar.month', {
            year: String(cursor.getFullYear()),
            month: String(cursor.getMonth() + 1),
          })}
        </p>
        <button
          onClick={() => shift(1)}
          disabled={!canNext}
          aria-label={t('reservationCalendar.nextMonth')}
          className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 tap-effect disabled:opacity-30"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1">
        {[0, 1, 2, 3, 4, 5, 6].map((d) => (
          <div
            key={d}
            className={`text-center font-body text-[10px] py-1 ${
              d === 0 ? 'text-yokoso-red' : d === 6 ? 'text-navy' : 'text-text-tertiary'
            }`}
          >
            {t(`storeSettings.reservationDayShort.${d}`)}
          </div>
        ))}

        {cells.map((d, i) => {
          if (!d) return <div key={`pad-${i}`} />
          const dateStr = toDateStr(d)
          const enabled = isEnabled(dateStr)
          const selected = value === dateStr
          return (
            <button
              key={dateStr}
              onClick={() => enabled && onChange(dateStr)}
              disabled={!enabled}
              className={`aspect-square rounded-lg font-body text-xs font-medium transition-colors ${
                selected
                  ? 'bg-navy text-white font-bold'
                  : enabled
                    ? 'bg-gray-100 text-text-primary tap-effect'
                    : 'text-gray-300 cursor-not-allowed'
              }`}
            >
              {d.getDate()}
            </button>
          )
        })}
      </div>
    </div>
  )
}
